import 'react';
import {Box, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {I21Page} from '@/pages/components/audit-program/i-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface I21SystemTestViewProps {
    header: AuditProgramSectionVO;
    page: I21Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

/** I2-1 — Inventory system testing (objectives + working notes; sample tables filled on WP). */
export default function I21SystemTestView({
    header,
    page,
    refCode = 'I2-1',
    values,
    onChange,
}: I21SystemTestViewProps) {
    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                Objectives:
            </Typography>
            <Stack spacing={0.5} sx={{mb: 2}}>
                {page.objectives.map((obj, idx) => (
                    <Typography key={idx} variant="body2" sx={{pl: obj.startsWith('(i)') ? 2 : 0, lineHeight: 1.45}}>
                        {obj}
                    </Typography>
                ))}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{mb: 2, lineHeight: 1.45}}>
                {page.workHint}
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                Testing notes / result
            </Typography>
            <InputField
                type="text"
                fullWidth
                multiline
                minRows={6}
                value={values.notes ?? ''}
                onChange={(e) => onChange('notes', e.target.value)}
                sx={{mb: 2}}
            />

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                Legend / conclusion
            </Typography>
            <Stack spacing={0.5} sx={{mb: 1}}>
                {page.legend.map((line, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary" sx={{lineHeight: 1.4}}>
                        {line}
                    </Typography>
                ))}
            </Stack>
            <InputField
                type="text"
                fullWidth
                compact
                value={values.result ?? ''}
                onChange={(e) => onChange('result', e.target.value)}
                placeholder="Result"
            />
        </Box>
    );
}
